// Sidebar list tree.
//
//   ▼ queue (12)
//       The Matrix (1999)
//       Blade Runner (1982)
//   ▸ watched (48)          ← rows show the rating right-aligned in accent
//   ▸ ignore (7)
//
// Selection state lives in URL search params (?list=queue&title=tt0133093) so
// deep-linking to a selected title survives reloads. Expanded state persists
// to localStorage under "cinedb.tree.expanded".

import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useListItems, useLists } from "../api/hooks";
import {
  type FilterState,
  filterLabel,
  filterOptions,
  SORT_FIELD_LABEL,
  type SortState,
  viewItems,
} from "../api/sort";
import { LIST_NAMES, type ListItem, type ListName } from "../api/types";

import "./ListTree.css";

const EXPANDED_STORAGE_KEY = "cinedb.tree.expanded";

function loadExpanded(): Record<string, boolean> {
  if (typeof window === "undefined") return { queue: true };
  try {
    const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
    // First ever visit: open the queue — it's the list to triage.
    if (raw === null) return { queue: true };
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return { queue: true };
  }
}

function saveExpanded(map: Record<string, boolean>): void {
  window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(map));
}

export function ListTree({
  sort,
  filter,
  onCycleField,
  onToggleDir,
  onSetFilter,
}: {
  sort: SortState;
  filter: FilterState | null;
  onCycleField: () => void;
  onToggleDir: () => void;
  onSetFilter: (value: string | null) => void;
}) {
  const { data: counts, error, isLoading } = useLists();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => loadExpanded());
  const [filterOpen, setFilterOpen] = useState(false);

  function toggle(name: ListName) {
    setExpanded((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      saveExpanded(next);
      return next;
    });
  }

  const toolbar = (
    <div className="tree-sort">
      <div className="tree-filter">
        <button
          type="button"
          className={"tree-filter__btn" + (filter ? " tree-filter__btn--active" : "")}
          onClick={() => setFilterOpen((o) => !o)}
          title="filter the lists by the current sort category"
        >
          ▾ {filter ? filterLabel(filter) : "filter"}
        </button>
        {filterOpen && (
          <>
            <div className="tree-filter__overlay" onClick={() => setFilterOpen(false)} />
            <div className="tree-filter__menu" role="menu">
              <button
                type="button"
                className={"tree-filter__item" + (!filter ? " tree-filter__item--active" : "")}
                onClick={() => {
                  onSetFilter(null);
                  setFilterOpen(false);
                }}
              >
                all
              </button>
              {filterOptions(sort.field).map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={
                    "tree-filter__item" +
                    (filter?.value === o.value ? " tree-filter__item--active" : "")
                  }
                  onClick={() => {
                    onSetFilter(o.value);
                    setFilterOpen(false);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        className="tree-sort__field"
        onClick={onCycleField}
        title="sort field — click to cycle: A–Z / year / rating"
      >
        sort: {SORT_FIELD_LABEL[sort.field]}
      </button>
      <button
        type="button"
        className="tree-sort__dir"
        onClick={onToggleDir}
        title={sort.dir === "asc" ? "ascending — click for descending" : "descending — click for ascending"}
        aria-label={sort.dir === "asc" ? "ascending" : "descending"}
      >
        {sort.dir === "asc" ? "↑" : "↓"}
      </button>
    </div>
  );

  if (error) {
    return (
      <>
        {toolbar}
        <div className="tree-empty">couldn't load lists</div>
      </>
    );
  }
  if (isLoading || !counts) {
    return (
      <>
        {toolbar}
        <div className="tree-empty">loading…</div>
      </>
    );
  }

  return (
    <>
      {toolbar}
      <ul className="tree" role="tree">
        {LIST_NAMES.map((name) => (
          <ListNode
            key={name}
            name={name}
            count={counts[name]?.count ?? 0}
            expanded={!!expanded[name]}
            onToggle={() => toggle(name)}
            sort={sort}
            filter={filter}
          />
        ))}
      </ul>
    </>
  );
}

function ListNode({
  name,
  count,
  expanded,
  onToggle,
  sort,
  filter,
}: {
  name: ListName;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  sort: SortState;
  filter: FilterState | null;
}) {
  const { data, error, isLoading } = useListItems(expanded ? name : null);
  const [params, setParams] = useSearchParams();
  const selectedList = params.get("list");
  const selectedTitle = params.get("title");
  const items = data ? viewItems(data.items, sort, filter) : [];

  function selectTitle(item: ListItem) {
    const next = new URLSearchParams(params);
    next.set("list", name);
    next.set("title", item.imdb_id);
    setParams(next, { replace: true });
  }

  return (
    <li className="tree__list" role="treeitem" aria-expanded={expanded}>
      <div
        className="tree__list-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="tree__chevron" aria-hidden>
          {expanded ? "▼" : "▶"}
        </span>
        <span className="tree__list-name">{name}</span>
        <span className="tree__list-count">({count})</span>
      </div>

      {expanded && (
        <ul className="tree__titles" role="group">
          {isLoading && <li className="tree__empty-titles">loading…</li>}
          {error && <li className="tree__empty-titles">couldn't load {name}</li>}
          {data && items.length === 0 && (
            <li className="tree__empty-titles">
              {filter && data.items.length > 0 ? "no matches for this filter" : "nothing here"}
            </li>
          )}
          {items.map((item) => {
            const selected = selectedList === name && selectedTitle === item.imdb_id;
            return (
              <TitleRow
                key={item.imdb_id}
                item={item}
                list={name}
                selected={selected}
                onSelect={() => selectTitle(item)}
              />
            );
          })}
        </ul>
      )}
    </li>
  );
}

function TitleRow({
  item,
  list,
  selected,
  onSelect,
}: {
  item: ListItem;
  list: ListName;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li
      className={`tree__title${selected ? " tree__row--selected" : ""}`}
      role="treeitem"
      aria-selected={selected}
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="tree__title-row">
        <span className="tree__title-name">
          {item.title}
          {item.year != null ? ` (${item.year})` : ""}
        </span>
        {list === "watched" && item.rating != null && (
          <span className="tree__title-rating">{item.rating}</span>
        )}
      </div>
    </li>
  );
}
